import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignInFlow } from '@mystudioclass/auth';
import { Video, Eye, EyeOff, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function SignInPage() {
  // Force light mode on auth pages
  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
  }, []);
  const { isLoaded, createSignIn, error } = useSignInFlow();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLoaded) return;

    setLoading(true);

    try {
      // Use Better Auth sign-in
      const result = await createSignIn({
        identifier: formData.email,
        password: formData.password
      });

      // Double-check for errors in result (fallback check)
      if (result?.error) {
        throw new Error(result.error.message || 'Invalid email or password');
      }

      // Only show success if we actually have user data
      if (!result?.data?.user && !result?.data?.session) {
        throw new Error('Invalid email or password');
      }

      toast.success('Welcome back!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Sign in error:', err);
      const errorMessage = err.message || error?.message || 'Invalid email or password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-base)' }}>
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="https://mystudioclass.com" className="inline-block">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4 transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))' }}
            >
              <Video className="w-8 h-8" style={{ color: 'var(--bg-base)' }} />
            </div>
          </a>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>My Studio Class</h1>
          <p className="mt-2" style={{ color: 'var(--text-muted)' }}>Business Portal</p>
        </div>

        {/* Login Form */}
        <div className="rounded-2xl shadow-xl p-8" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
          <div className="flex items-center justify-center mb-6 p-3 rounded-lg" style={{ background: 'var(--primary-10)', border: '1px solid var(--primary-20)' }}>
            <Building2 className="w-5 h-5 mr-2" style={{ color: 'var(--primary)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Business Sign In</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="label">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                placeholder="admin@yourstudio.com"
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <label className="label">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-sm transition-colors"
                  style={{ color: 'var(--primary)' }}
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="input pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !isLoaded}
              className="btn btn-primary w-full"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Don't have an account?{' '}
              <Link to="/sign-up" className="font-medium transition-colors" style={{ color: 'var(--primary)' }}>
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
